import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, Star, ExternalLink } from 'lucide-react';
import { imageCache } from '../utils/imageCache';

const EventCard = ({ event, index }) => {
  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha por definir';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para formatear la hora
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para determinar si el evento es próximo (dentro de 7 días)
  const isUpcoming = (dateString) => {
    if (!dateString) return false;
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Función para obtener el estado del evento
  const getEventStatus = (dateString) => {
    if (!dateString) return { text: 'Por definir', color: 'gray' };
    
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Finalizado', color: 'gray' };
    } else if (diffDays === 0) {
      return { text: '¡Hoy!', color: 'red' };
    } else if (diffDays === 1) {
      return { text: 'Mañana', color: 'orange' };
    } else if (diffDays <= 7) {
      return { text: `En ${diffDays} días`, color: 'green' };
    } else {
      return { text: 'Próximamente', color: 'blue' };
    }
  };

  const status = getEventStatus(event.event_date);
  const beaconsAiLink = "https://beacons.ai/miamarketfood";

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 cursor-pointer h-full flex flex-col"
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ scale: 1.03, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }}
    >
      {/* Imagen del evento */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageCache.getCachedUrl(event.image_url || 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&h=600&fit=crop')}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&h=600&fit=crop';
          }}
        />
        
        {/* Badge de estado */}
        <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white ${
          status.color === 'red' ? 'bg-red-500' :
          status.color === 'orange' ? 'bg-orange-500' :
          status.color === 'green' ? 'bg-green-500' :
          status.color === 'blue' ? 'bg-blue-500' :
          'bg-gray-500'
        }`}>
          {status.text}
        </div>

        {/* Overlay con gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        
        {/* Icono de evento */}
        <div className="absolute top-4 right-4 bg-blue-500 p-2 rounded-full">
          <Calendar className="w-4 h-4 text-white" />
        </div>
      </div>

      {/* Contenido del evento */}
      <div className="p-6 flex flex-col flex-grow">
        {/* Título */}
        <h3 className="text-xl font-bold text-gray-800 mb-2" style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '3.5rem'
        }}>
          {event.title}
        </h3>

        {/* Descripción */}
        <p className="text-gray-600 text-sm mb-4 flex-grow" style={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {event.description}
        </p>

        {/* Información del evento */}
        <div className="space-y-2 mb-4">
          {/* Fecha y hora */}
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="w-4 h-4 mr-2 text-blue-500" />
            <span className="font-medium">
              {formatDate(event.event_date)}
            </span>
            {formatTime(event.event_date) && (
              <>
                <Clock className="w-4 h-4 ml-4 mr-2 text-green-500" />
                <span>{formatTime(event.event_date)}</span>
              </>
            )}
          </div>

          {/* Ubicación */}
          {event.location && (
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="w-4 h-4 mr-2 text-red-500" />
              <span>{event.location}</span>
            </div>
          )}

          {/* Capacidad */}
          {event.capacity && (
            <div className="flex items-center text-sm text-gray-600">
              <Users className="w-4 h-4 mr-2 text-purple-500" />
              <span>Capacidad: {event.capacity} personas</span>
            </div>
          )}
        </div>

        {/* Información adicional */}
        <div className="flex items-center justify-between mb-4">
          {/* Precio */}
          <div className="flex items-center">
            {event.is_free ? (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                ¡Gratis!
              </span>
            ) : event.price ? (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                S/ {event.price}
              </span>
            ) : null}
          </div>

          {/* Registro requerido */}
          {event.registration_required && (
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium">
              Registro requerido
            </span>
          )}
        </div>

        {/* Botón de acción - siempre al final */}
        <motion.a
          href={beaconsAiLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full py-3 px-4 rounded-xl font-semibold text-center flex items-center justify-center gap-2 transition-all duration-300 mt-auto ${
            event.registration_required
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
          } text-white`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Star className="w-4 h-4" />
          {event.registration_required ? 'Registrarse' : 'Más información'}
          <ExternalLink className="w-4 h-4" />
        </motion.a>
      </div>

      {/* Indicador de evento destacado */}
      {isUpcoming(event.event_date) && (
        <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-400 to-orange-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
          ¡Próximo!
        </div>
      )}
    </motion.div>
  );
};

export default EventCard;
